import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";
import { trackHeapEvent } from "@/helpers/heap/trackHeapEvent";

type HeapProperties = Record<string, string | number | boolean | null>;

type EventDescriptor<TData, TArgs> =
  | string
  | {
      name: string;
      enable?:
        | boolean
        | ((res: TData, data: TArgs) => Promise<boolean> | boolean);
      sendEvent?: (res: TData, data: TArgs) => void | Promise<void>;
      getMetaData?: (
        res: TData,
        data: TArgs
      ) => Promise<HeapProperties> | HeapProperties;
    };

export const useWithTrackingMutation = <
  TArgs,
  TData extends { status: number },
>(
  mutationFn: (args: TArgs) => Promise<TData>,
  events: EventDescriptor<TData, TArgs>[],
  options?: UseMutationOptions<TData, Error, TArgs>
): UseMutationResult<TData, Error, TArgs> => {
  return useMutation({
    mutationFn: async (args) => {
      const res = await mutationFn(args);
      if (res?.status === 200 || res.status === 201) {
        for (const event of events) {
          if (typeof event === "string") {
            trackHeapEvent(event);
          } else {
            if (event.sendEvent) {
              void event.sendEvent(res, args);
              continue;
            }
            if (
              event.enable !== undefined &&
              ((typeof event.enable === "boolean" && !event.enable) ||
                (typeof event.enable !== "boolean" &&
                  !(await event.enable(res, args))))
            ) {
              continue;
            }
            const meta = event.getMetaData
              ? await event.getMetaData(res, args)
              : undefined;
            trackHeapEvent(event.name, meta);
          }
        }
      }
      return res;
    },
    ...options,
  });
};
